import type { IrrigationEventResponse, ListIrrigationEvents200 } from "@/api/orval/generated/schemas";
import { listItems } from "@/api/query-cache/collection-cache-writers";

/**
 * The generated setter matches on the `["irrigation-events"]` prefix, which also covers the
 * set-filtered lists and the single-event queries. `replaceCachedIrrigationEvent` and
 * `removeCachedIrrigationEvent` leave a cache entry alone unless it already holds the event, so a
 * filtered list can't gain one that doesn't belong in it.
 *
 * `addCachedIrrigationEvent` can't tell which entry it landed in, so it is only safe through
 * `setQueryData` on the unfiltered list's exact key.
 */

export function replaceCachedIrrigationEvent(event: IrrigationEventResponse) {
    return (cached: ListIrrigationEvents200 | undefined): ListIrrigationEvents200 | undefined => {
        // Without a recordId there is nothing to match on, and matching two absent ones would
        // overwrite an unrelated event.
        const cachedItems = listItems(cached);
        if (!cached || !cachedItems || event.recordId === undefined) {
            return cached;
        }
        let held = false;
        const items = cachedItems.map((item) => {
            if (item.recordId === event.recordId) {
                held = true;
                return event;
            }
            return item;
        });
        return held ? { ...cached, items } : cached;
    };
}

export function removeCachedIrrigationEvent(recordId: string) {
    return (cached: ListIrrigationEvents200 | undefined): ListIrrigationEvents200 | undefined => {
        const cachedItems = listItems(cached);
        if (!cached || !cachedItems) {
            return cached;
        }
        const items = cachedItems.filter((item) => item.recordId !== recordId);
        return items.length === cachedItems.length ? cached : { ...cached, items };
    };
}

export function addCachedIrrigationEvent(event: IrrigationEventResponse) {
    return (cached: ListIrrigationEvents200 | undefined): ListIrrigationEvents200 | undefined => {
        const cachedItems = listItems(cached);
        if (!cached || !cachedItems) {
            return cached;
        }
        // The write awaits a refetch that usually lands the event first, so this is normally a no-op.
        if (event.recordId !== undefined && cachedItems.some((item) => item.recordId === event.recordId)) {
            return cached;
        }
        return { ...cached, items: [...cachedItems, event] };
    };
}
